import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useOrderStore } from '../../store/orderStore'
import api from '../../api/axiosConfig'
import './UnifiedCheckout.css'
import SupportSection from './SupportSection'

export default function UnifiedCheckout() {
    const { selectedPackage, developerAccess, getTotalPrice, walletAddress, setWalletAddress, network, setNetwork } = useOrderStore()
    const [isProcessing, setIsProcessing] = useState(false)
    const [error, setError] = useState('')
    const [addressError, setAddressError] = useState('')

    const networks = [
        'Tron (TRC20)',
        'Ethereum (ERC20)'
    ]

    // Payment Providers High-Level Selection
    const [paymentProvider, setPaymentProvider] = useState<'coinbase' | 'manual' | null>(null)

    const paymentProviders = [
        {
            id: 'coinbase',
            name: 'Coinbase Commerce',
            description: 'Pay with ETH, USDC, Polygon, Base, or DAI',
            icon: (
                <svg viewBox="0 0 1024 1024" className="provider-icon-svg">
                    <path d="M512 0C229.2 0 0 229.2 0 512s229.2 512 512 512 512-229.2 512-512S794.8 0 512 0zm0 896c-212 0-384-172-384-384S300 128 512 128s384 172 384 384-172 384-384 384z" fill="#0052FF" />
                    <path d="M512 256c-141.4 0-256 114.6-256 256s114.6 256 256 256 256-114.6 256-256-114.6-256-256-256z" fill="#0052FF" />
                </svg>
            ),
            badge: 'Auto'
        },
        {
            id: 'manual',
            name: 'Manual Payment',
            description: 'Direct blockchain transfer (USDT, BTC, SOL)',
            icon: (
                <svg viewBox="0 0 24 24" className="provider-icon-svg" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            ),
            badge: 'Direct'
        }
    ]

    // Validate wallet address based on network
    const validateAddress = (address: string, selectedNetwork: string): boolean => {
        if (!address) return false

        // Tron (TRC20) addresses start with 'T' and are 34 characters
        if (selectedNetwork === 'Tron (TRC20)') {
            const tronRegex = /^T[A-Za-z1-9]{33}$/
            return tronRegex.test(address)
        }

        // Ethereum (ERC20) addresses start with '0x' and are 42 characters (including 0x)
        if (selectedNetwork === 'Ethereum (ERC20)') {
            const ethRegex = /^0x[a-fA-F0-9]{40}$/
            return ethRegex.test(address)
        }

        return false
    }

    // Handle wallet address change with auto-detection and validation
    const handleWalletAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const address = e.target.value.trim()
        setWalletAddress(address)

        let activeNetwork = network

        // Auto-detect network based on address prefix
        if (address.startsWith('T')) {
            activeNetwork = 'Tron (TRC20)'
            if (network !== activeNetwork) setNetwork(activeNetwork)
        } else if (address.startsWith('0x')) {
            activeNetwork = 'Ethereum (ERC20)'
            if (network !== activeNetwork) setNetwork(activeNetwork)
        }

        if (address) {
            if (!validateAddress(address, activeNetwork)) {
                if (activeNetwork === 'Tron (TRC20)') {
                    setAddressError('Invalid Tron address. Must start with "T" and be 34 characters.')
                } else if (activeNetwork === 'Ethereum (ERC20)') {
                    setAddressError('Invalid Ethereum address. Must start with "0x" and be 42 characters.')
                }
            } else {
                setAddressError('')
            }
        } else {
            setAddressError('')
        }
    }

    // Handle network change and revalidate address
    const handleNetworkChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newNetwork = e.target.value
        setNetwork(newNetwork)

        // Revalidate address with new network
        if (walletAddress) {
            if (!validateAddress(walletAddress, newNetwork)) {
                if (newNetwork === 'Tron (TRC20)') {
                    setAddressError('Invalid Tron address. Must start with "T" and be 34 characters.')
                } else if (newNetwork === 'Ethereum (ERC20)') {
                    setAddressError('Invalid Ethereum address. Must start with "0x" and be 42 characters.')
                }
            } else {
                setAddressError('')
            }
        }
    }

    // Address is required if a Flash Plan package is selected
    // It is OPTIONAL if only Developer Access is selected
    const isAddressRequired = !!selectedPackage

    const handleCheckout = async () => {
        // Validate package selection
        if (!selectedPackage && !developerAccess) {
            setError('Please select a package or developer access')
            return
        }

        // Validate wallet address (only if required)
        if (isAddressRequired) {
            if (!walletAddress) {
                setError('Please enter your wallet address')
                return
            }

            // Validate address format for selected network
            if (!validateAddress(walletAddress, network)) {
                setError('Please enter a valid wallet address for the selected network')
                return
            }
        }

        // Validate payment provider selection
        if (!paymentProvider) {
            setError('Please select a payment method')
            return
        }

        setIsProcessing(true)
        setError('')

        try {
            const totalPrice = getTotalPrice()

            // Store order data for the complete payment page / resume flows
            try {
                localStorage.setItem(
                    'flashusdt:order_data',
                    JSON.stringify({
                        packageId: selectedPackage?.id,
                        packageName: selectedPackage?.name,
                        packageAmount: selectedPackage?.amount,
                        packagePrice: selectedPackage?.price,
                        devAccessId: developerAccess?.id,
                        devAccessName: developerAccess?.name,
                        devAccessPrice: developerAccess?.price,
                        totalPrice: totalPrice,
                        walletAddress: isAddressRequired ? walletAddress : '',
                        network: isAddressRequired ? network : '',
                        // payAsset is no longer chosen here for manual; inferred by backend for delivery network
                        // For Coinbase, it handles it content internally
                        createdAt: Date.now(),
                    }),
                )
            } catch {
                // ignore storage failures (private mode, etc.)
            }

            // Route based on what the user wants to PAY with:
            if (paymentProvider === 'coinbase') {
                const payload: {
                    items?: Array<{ productId: string; quantity: number }>;
                    customOptions?: { amount?: number };
                    deliveryAddress?: string;
                    deliveryNetwork?: string;
                } = {}

                // Only attach delivery details if required
                if (isAddressRequired) {
                    payload.deliveryAddress = walletAddress
                    payload.deliveryNetwork = network
                }

                const items = []

                if (selectedPackage) {
                    if (selectedPackage.id !== 'custom') {
                        items.push({ productId: selectedPackage.id, quantity: 1 })
                    } else {
                        // For custom, we can't easily mix with standard products in this simplified payload structure
                        // unless backend supports it. Assuming custom is standalone or primary for now.
                        payload.customOptions = { amount: selectedPackage.amount }
                    }
                }

                if (developerAccess) {
                    items.push({ productId: developerAccess.id, quantity: 1 })
                }

                if (items.length > 0) {
                    payload.items = items
                }

                const chargeResponse = await api.post('/api/payments/coinbase/create-charge', payload)
                const hostedUrl = chargeResponse.data?.hostedUrl
                if (!hostedUrl) throw new Error('Missing hostedUrl from Coinbase charge creation')
                window.location.href = hostedUrl
                return
            }

            // Manual route
            window.location.href = '/payment/complete'

        } catch (err: any) {
            console.error('Checkout error:', err)
            setError(err.response?.data?.message || 'Failed to initiate checkout')
            setIsProcessing(false)
        }
    }

    return (
        <>
            <div className="unified-checkout">
                <motion.div
                    className="checkout-container glass"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="checkout-header">
                        <h3 className="checkout-title">Secure Checkout</h3>
                        <div className="secure-badge">
                            <span className="lock-icon">🔒</span>
                            <span>SSL Encrypted</span>
                        </div>
                    </div>

                    <div className="checkout-content">
                        {/* Selected Items Summary */}


                        {/* Payment Provider Info */}


                        {/* Wallet Address Input (Conditionally Rendered) */}
                        <AnimatePresence>
                            {isAddressRequired && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="overflow-hidden"
                                >
                                    <div className="wallet-input-section">
                                        <label htmlFor="wallet-address" className="input-label">
                                            Delivery Wallet Address *
                                        </label>
                                        <input
                                            type="text"
                                            id="wallet-address"
                                            className={`wallet-input ${addressError ? 'error' : walletAddress && !addressError ? 'valid' : ''}`}
                                            placeholder={network === 'Tron (TRC20)' ? 'T...' : '0x...'}
                                            value={walletAddress}
                                            onChange={handleWalletAddressChange}
                                        />
                                        {addressError && (
                                            <p className="input-error">
                                                <span className="error-icon">⚠️</span>
                                                {addressError}
                                            </p>
                                        )}
                                        {!addressError && walletAddress && (
                                            <p className="input-success">
                                                <span className="success-icon">✓</span>
                                                Valid {network.split(' ')[0]} address
                                            </p>
                                        )}
                                        {!walletAddress && (
                                            <p className="input-hint">USDT will be delivered to this address after payment</p>
                                        )}
                                    </div>

                                    {/* Network Selector */}
                                    <div className="network-selector-section">
                                        <label htmlFor="network" className="input-label">
                                            Delivery Network *
                                        </label>
                                        <select
                                            id="network"
                                            className="network-select"
                                            value={network}
                                            onChange={handleNetworkChange}
                                        >
                                            {networks.map((net) => (
                                                <option key={net} value={net}>
                                                    {net}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="input-hint">
                                            {network === 'Tron (TRC20)'
                                                ? 'Address must start with "T" (34 characters)'
                                                : 'Address must start with "0x" (42 characters)'}
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Payment Options Info */}
                        <div className="payment-options-info">
                            <h4>Pay With:</h4>
                            <div className="payment-providers-grid">
                                {paymentProviders.map((provider) => {
                                    const selected = paymentProvider === provider.id
                                    return (
                                        <motion.div
                                            key={provider.id}
                                            className={`payment-provider-card ${selected ? 'selected' : ''}`}
                                            onClick={() => setPaymentProvider(provider.id as any)}
                                            whileHover={{ y: -2 }}
                                            whileTap={{ scale: 0.99 }}
                                        >
                                            <div className="provider-card-header">
                                                <div className="provider-icon-wrapper">
                                                    {provider.icon}
                                                </div>
                                                <div className="provider-badge">{provider.badge}</div>
                                            </div>
                                            <div className="provider-card-content">
                                                <h5>{provider.name}</h5>
                                                <p>{provider.description}</p>
                                            </div>
                                            {selected && (
                                                <motion.div
                                                    className="selected-indicator"
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none">
                                                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </motion.div>
                                            )}
                                        </motion.div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* How it Works */}
                        <div className="how-it-works">
                            <h4>How it works:</h4>
                            <div className="step-item">
                                <div className="step-number">1</div>
                                <p>Select your preferred payment method type</p>
                            </div>
                            <div className="step-item">
                                <div className="step-number">2</div>
                                <p>Click “Proceed to Checkout”</p>
                            </div>
                            <div className="step-item">
                                <div className="step-number">3</div>
                                <p>Follow the instructions to complete your payment securely</p>
                            </div>
                            <div className="step-item">
                                <div className="step-number">4</div>
                                <p>USDT delivered instantly to your wallet</p>
                            </div>
                        </div>

                        {/* Error Display */}
                        {error && (
                            <div className="error-message">
                                <span className="error-icon">⚠️</span>
                                {error}
                            </div>
                        )}

                        {/* Checkout Button */}
                        <button
                            className={`btn btn-primary checkout-btn ${isProcessing ? 'processing' : ''}`}
                            onClick={handleCheckout}
                            disabled={isProcessing || (!selectedPackage && !developerAccess) || (isAddressRequired && !walletAddress) || !!addressError || !paymentProvider}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner"></span>
                                    Starting Checkout...
                                </>
                            ) : (
                                <>
                                    Proceed to Checkout
                                    {` • $${getTotalPrice().toLocaleString()}`}
                                </>
                            )}
                        </button>

                        {/* Security Badges */}
                        <div className="security-badges">
                            <div className="badge">
                                <span className="badge-icon">✓</span>
                                <span>Fraud Protected</span>
                            </div>
                            <div className="badge">
                                <span className="badge-icon">🔒</span>
                                <span>256-bit SSL</span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Support Section */}
                <SupportSection />
            </div>
        </>
    )
}
